import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Grid,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    CardActions,
    useMediaQuery,
    CircularProgress,
    Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import { getPlanFeaturesInOrder } from '../../utils/planFeatureDisplay';

const formatPrice = (cents) => {
    if (cents == null || cents === 0) return '$0';
    return `$${(cents / 100).toFixed(0)}`;
};

/** Fallback if API fails (matches prior hardcoded signup page). */
const DEFAULT_PLAN_PRICES = {
    B: { monthly: '$0', annual: '$0' },
    S: { monthly: '$79', annual: '$59' },
    P: { monthly: '$199', annual: '$149' },
    U: { monthly: '$499', annual: '$374' }
};

export default function Signup() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Pricing mode state: 'monthly' (default) or 'annual'
    const [pricingMode, setPricingMode] = useState('monthly');
    const [planPrices, setPlanPrices] = useState(DEFAULT_PLAN_PRICES);
    const [plans, setPlans] = useState([]);
    const [plansError, setPlansError] = useState(null);
    const [plansLoading, setPlansLoading] = useState(true);

    useEffect(() => {
        axios
            .get(`${API_URL}/account/plans`)
            .then((res) => {
                if (res.data.success && Array.isArray(res.data.plans) && res.data.plans.length > 0) {
                    setPlansError(null);
                    setPlans(res.data.plans);
                    const prices = { ...DEFAULT_PLAN_PRICES };
                    res.data.plans.forEach((p) => {
                        prices[p.value] = {
                            monthly: formatPrice(p.monthlyPriceCents),
                            annual: formatPrice(p.annualPriceCents)
                        };
                    });
                    setPlanPrices(prices);
                } else {
                    setPlansError(res.data?.message || 'No plans available');
                }
            })
            .catch((err) => {
                console.error('Signup: failed to fetch plan prices', err?.response?.data || err);
                setPlansError(err?.response?.data?.message || err?.message || 'Failed to load plans');
            })
            .finally(() => setPlansLoading(false));
    }, []);

    // Function to handle plan selection
    const handleSelectPlan = (planType) => {
        // include current pricingMode (monthly | annual) so signup flow can persist billing_cycle
        navigate(`/signup/form?plan=${planType}&billing_cycle=${pricingMode}`);
    };

    return (
            <Container
                maxWidth="lg"
                sx={{
                    mt: 4,
                    mb: 24,
                    overflow: 'visible',
                    position: 'relative',
                    zIndex: 1
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        mb: 3,
                        mt: 4,
                        color: '#23272F',
                        letterSpacing: 0.5
                    }}
                >
                    Simple, Flexible Pricing
                </Typography>
                <Typography
                    variant="h5"
                    align="center"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        mb: 6,
                        lineHeight: 1.5,
                        px: { xs: 0.5, sm: 0 }
                    }}
                >
                    Start with a two-week free trial with no credit card required. After that, you can choose a monthly subscription that you can cancel at any time. With an annual commitment, you can get an instant 25% discount.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Button
                        variant={pricingMode === 'monthly' ? 'contained' : 'outlined'}
                        color="primary"
                        sx={{ mr: 1, fontWeight: 700 }}
                        onClick={() => setPricingMode('monthly')}
                    >
                        Monthly Pricing
                    </Button>
                    <Button
                        variant={pricingMode === 'annual' ? 'contained' : 'outlined'}
                        color="primary"
                        sx={{ fontWeight: 700 }}
                        onClick={() => setPricingMode('annual')}
                    >
                        Annual Pricing
                    </Button>
                </Box>
                {plansLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 420, py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : plans.length === 0 ? (
                    <Alert severity="error" sx={{ maxWidth: 560, mx: 'auto' }}>
                        {plansError || 'No plans available. Please try again later.'}
                    </Alert>
                ) : (
                <Grid
                    container
                    spacing={2}
                    justifyContent="center"
                    alignItems="stretch"
                    wrap={isMobile ? 'wrap' : 'nowrap'}
                >
                    {plans.map((plan) => {
                        const isFreeTrial = plan.value === 'B';
                        const displayName = isFreeTrial ? 'Free Trial' : (plan.name || plan.value);
                        const features = getPlanFeaturesInOrder(plan);
                        const highlighted = !!plan.highlight;
                        return (
                            <Grid item xs={12} sm={6} md={3} key={plan.value} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        position: 'relative',
                                        minHeight: 420,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        borderColor: 'divider',
                                        boxShadow: isFreeTrial ? 6 : 0,
                                        width: '100%',
                                        pt: isFreeTrial ? 2 : 2,
                                        pb: 2,
                                        mt: 0,
                                        mb: isFreeTrial ? -4 : 0,
                                        background: isFreeTrial ? (t) => t.palette.primary.main : (highlighted ? 'linear-gradient(180deg, #f5fbff 80%, #fff 100%)' : '#fff'),
                                        borderTop: highlighted && !isFreeTrial ? (t) => `5px solid ${t.palette.primary.main}` : undefined,
                                        zIndex: isFreeTrial ? 2 : 1,
                                        overflow: 'visible',
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 0, pt: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        {isFreeTrial ? (
                                            <Box sx={{ mb: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
                                                <Box sx={{ background: 'red', color: '#fff', borderRadius: 1, px: 2.5, py: 0.5, fontWeight: 700, fontSize: 18, letterSpacing: 1, boxShadow: 2, display: 'inline-block' }}>
                                                    Free Trial
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5, mt: 0 }}>
                                                {plan.name}
                                            </Typography>
                                        )}
                                        <Typography variant="h3" sx={{ fontWeight: 700, color: isFreeTrial ? '#fff' : 'primary.main', mb: 0.5, mt: 0 }}>
                                            {(planPrices[plan.value] && planPrices[plan.value][pricingMode]) || '$0'}
                                            {isFreeTrial ? '' : (
                                                <Typography variant="h6" component="span" sx={{ fontWeight: 400, ml: 1, fontSize: 18, color: 'primary.main' }}>/mo</Typography>
                                            )}
                                        </Typography>
                                        {plan.priceNote && (
                                            <Typography variant="subtitle2" sx={{ color: isFreeTrial ? '#fff' : (highlighted ? 'primary.main' : 'text.secondary'), fontWeight: highlighted ? 700 : 400, mb: 1, mt: 0 }}>{plan.priceNote}</Typography>
                                        )}
                                        {plan.description && (
                                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isFreeTrial ? '#fff' : 'text.secondary' }}>{plan.description}</Typography>
                                        )}
                                        <Box sx={{ width: '100%', mt: 1 }}>
                                            {features.map((feature, idx) => (
                                                <React.Fragment key={idx}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5 }}>
                                                        <Typography variant="body1" sx={{ color: isFreeTrial ? '#fff' : '#c9c9c9', fontWeight: 500, fontSize: 16 }}>{feature}</Typography>
                                                    </Box>
                                                    {idx < features.length - 1 && (
                                                        <Box sx={{ width: '85%', height: 2, bgcolor: isFreeTrial ? '#fff' : '#c9c9c9', opacity: 0.5, mx: 'auto', my: 1, borderRadius: 1 }} />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ mt: 'auto', width: '100%', px: 2, pt: 1, pb: 2, justifyContent: 'center' }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color={isFreeTrial ? 'inherit' : 'primary'}
                                            onClick={() => handleSelectPlan(plan.value)}
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: 16,
                                                py: 1,
                                                mx: 1,
                                                bgcolor: isFreeTrial ? '#fff' : undefined,
                                                color: isFreeTrial ? 'primary.main' : undefined,
                                                border: isFreeTrial ? '2px solid #fff' : undefined,
                                                '&:hover': isFreeTrial ? { bgcolor: '#e6e6e6', color: 'primary.main' } : undefined
                                            }}
                                        >
                                            Select {displayName}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
                )}
            </Container>
    );
}
